import { formatPrice, cart, initiateCheckout } from '../lib/commerce.ts';
import type { Product } from '../lib/commerce.ts';

export function renderStore(): string {
  return /* html */`
<div class="page-wrapper store-page">
  <div class="page-hero">
    <span class="section-label">Store</span>
    <h1 class="hero-title anim-fade-up">
      The Xaebia<br/><span class="gradient-text">Bazaar.</span>
    </h1>
    <p class="hero-mission anim-fade-up" style="animation-delay:0.1s">
      Apps, licenses, support packs, and services. Everything you pay for
      goes directly to independent software development.
    </p>
    <div class="store-cart-bar anim-fade-up" style="animation-delay:0.2s">
      <button id="cart-btn" class="btn btn-ghost" aria-label="View cart">
        🛒 Cart <span id="cart-count" class="cart-count">0</span>
      </button>
    </div>
  </div>

  <div class="page-section">
    <!-- Category filter -->
    <div class="filter-bar" role="group" aria-label="Filter products">
      <button class="filter-btn active" data-filter="all">All</button>
      <button class="filter-btn" data-filter="Software">Software</button>
      <button class="filter-btn" data-filter="Support">Support</button>
      <button class="filter-btn" data-filter="Merch">Merch</button>
      <button class="filter-btn" data-filter="Services">Services</button>
    </div>

    <div class="store-notice card" style="margin-top:var(--s6)">
      <span class="store-notice-icon">🔧</span>
      <div>
        <strong>Commerce coming soon.</strong> The store architecture is ready —
        we're connecting the payment backend now. Join the mailing list to be notified
        when products go live.
      </div>
    </div>

    <div id="store-grid" class="store-grid stagger" style="margin-top:var(--s8)">
      <!-- Populated by initStore() -->
    </div>
  </div>

  <!-- Cart drawer -->
  <div id="cart-drawer" class="cart-drawer hidden" role="dialog" aria-modal="true" aria-label="Shopping cart">
    <div class="cart-inner">
      <div class="cart-header">
        <h3>Cart</h3>
        <button id="cart-close" aria-label="Close cart">×</button>
      </div>
      <div id="cart-items" class="cart-items"></div>
      <div class="cart-footer">
        <div class="cart-total">Total: <strong id="cart-total-val">$0</strong></div>
        <button id="checkout-btn" class="btn btn-primary" style="width:100%">Checkout (Coming Soon)</button>
      </div>
    </div>
  </div>
</div>
`;
}

export function initStore(): void {
  import('../data/products.json').then(({ default: raw }) => {
    const products = raw as Product[];
    const grid = document.getElementById('store-grid');
    if (!grid) return;

    function render(filter: string): void {
      const filtered = filter === 'all'
        ? products
        : products.filter(p => p.category === filter);
      grid!.innerHTML = filtered.map(productCard).join('');
      grid!.querySelectorAll('.product-card').forEach(card => {
        card.addEventListener('click', () => {
          const id = (card as HTMLElement).dataset.productId;
          openProductDetail(products.find(p => p.id === id)!);
        });
      });
    }

    document.querySelectorAll('.filter-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.filter-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        render((btn as HTMLElement).dataset.filter ?? 'all');
      });
    });

    render('all');
    bindCartUI(products);
  });
}

function productCard(p: Product): string {
  return /* html */`
  <article class="product-card card" data-product-id="${p.id}" tabindex="0" role="button"
           aria-label="View ${p.name}" style="--product-color:${p.color}">
    ${p.featured ? '<div class="featured-ribbon">Featured</div>' : ''}

    <div class="product-icon-wrap" style="background:color-mix(in srgb,${p.color} 12%,transparent)">
      <span style="font-size:2rem">${p.icon}</span>
    </div>

    <h3 class="product-name">${p.name}</h3>
    <p class="product-tagline">${p.tagline}</p>

    <div class="product-price">${formatPrice(p.pricing)}</div>

    <div class="product-badges">
      <span class="badge badge-${typeColor(p.type)}">${p.type}</span>
      <span class="badge badge-${statusColor(p.status)}">${p.status}</span>
    </div>

    <div class="product-glow" style="--glow-color:${p.color}"></div>
  </article>
  `;
}

function openProductDetail(p: Product): void {
  const modal = document.createElement('div');
  modal.className = 'modal-overlay';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.innerHTML = /* html */`
  <div class="modal-content product-detail">
    <button class="modal-close" aria-label="Close">×</button>

    <div class="product-detail-header" style="--product-color:${p.color}">
      <span style="font-size:3rem">${p.icon}</span>
      <div>
        <h2>${p.name}</h2>
        <p>${p.tagline}</p>
        <div class="product-detail-price">${formatPrice(p.pricing)}</div>
      </div>
    </div>

    <p class="product-detail-desc">${p.description}</p>

    <ul class="product-features">
      ${p.features.map(f => `<li><span class="feature-check">✓</span> ${f}</li>`).join('')}
    </ul>

    <div class="app-detail-actions">
      ${p.status === 'Available'
        ? `<button class="btn btn-primary add-to-cart-btn" data-product-id="${p.id}">Add to Cart</button>`
        : `<button class="btn btn-ghost" disabled>${p.status}</button>`
      }
      ${p.demoUrl ? `<a href="${p.demoUrl}" class="btn btn-secondary" target="_blank" rel="noopener">Try Demo ↗</a>` : ''}
    </div>
  </div>
  `;

  modal.addEventListener('click', e => { if (e.target === modal) modal.remove(); });
  modal.querySelector('.modal-close')!.addEventListener('click', () => modal.remove());
  document.body.appendChild(modal);
  (modal.querySelector('.modal-close') as HTMLElement).focus();
}

function bindCartUI(products: Product[]): void {
  const btn = document.getElementById('cart-btn');
  const drawer = document.getElementById('cart-drawer');
  const closeBtn = document.getElementById('cart-close');
  const countEl = document.getElementById('cart-count');
  const itemsEl = document.getElementById('cart-items');
  const totalEl = document.getElementById('cart-total-val');
  const checkoutBtn = document.getElementById('checkout-btn');

  cart.subscribe(() => {
    const count = cart.count();
    if (countEl) countEl.textContent = String(count);
    if (countEl) countEl.style.display = count ? 'inline' : 'none';
    renderCartItems();
  });

  btn?.addEventListener('click', () => {
    drawer?.classList.toggle('hidden');
  });
  closeBtn?.addEventListener('click', () => {
    drawer?.classList.add('hidden');
  });

  checkoutBtn?.addEventListener('click', async () => {
    await initiateCheckout(cart.get());
  });

  document.addEventListener('click', e => {
    const t = e.target as HTMLElement;
    if (t.classList.contains('add-to-cart-btn')) {
      const id = t.dataset.productId;
      const product = products.find(p => p.id === id);
      if (product) {
        cart.add(product);
        t.textContent = 'Added!';
        setTimeout(() => t.textContent = 'Add to Cart', 1500);
      }
    }
  });

  function renderCartItems(): void {
    if (!itemsEl) return;
    const items = cart.get();
    if (items.length === 0) {
      itemsEl.innerHTML = '<p class="cart-empty">Your cart is empty.</p>';
    } else {
      itemsEl.innerHTML = items.map(item => `
        <div class="cart-item">
          <span>${item.product.icon} ${item.product.name}</span>
          <span>${formatPrice(item.product.pricing)}</span>
          <button class="cart-remove" data-id="${item.product.id}">×</button>
        </div>
      `).join('');
      itemsEl.querySelectorAll('.cart-remove').forEach(btn => {
        btn.addEventListener('click', () => {
          cart.remove((btn as HTMLElement).dataset.id!);
        });
      });
    }
    if (totalEl) totalEl.textContent = `$${cart.total()}`;
  }
}

function typeColor(t: string): string {
  if (t === 'License') return 'purple';
  if (t === 'Digital')  return 'teal';
  if (t === 'Physical') return 'amber';
  if (t === 'Service')  return 'pink';
  return 'dim';
}

function statusColor(s: string): string {
  if (s === 'Available')    return 'green';
  if (s === 'Coming Soon')  return 'amber';
  return 'dim';
}
